import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Edit, Trash2, UserCog, Shield, User, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UsersPage = () => {
  const { user, chapter } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";

  const [formData, setFormData] = useState({
    email: "", name: "", role: "member", password: ""
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setUsers(await res.json());
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.name) {
      toast.error("Email and name are required");
      return;
    }
    try {
      const url = editingUser ? `${API}/admin/users/${editingUser.user_id}` : `${API}/admin/users`;
      const method = editingUser ? "PUT" : "POST";
      const payload = editingUser
        ? { name: formData.name, role: formData.role, ...(formData.password ? { password: formData.password } : {}) }
        : formData;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success(editingUser ? "User updated" : "User created");
        setDialogOpen(false);
        resetForm();
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Operation failed");
      }
    } catch (error) {
      toast.error("Error saving user");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("User deleted");
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete user");
      }
    } catch (error) {
      toast.error("Error deleting user");
    }
  };

  const resetForm = () => {
    setFormData({ email: "", name: "", role: "member", password: "" });
    setEditingUser(null);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setFormData({ email: u.email, name: u.name, role: u.role, password: "" });
    setDialogOpen(true);
  };

  const roleIcons = { admin: Shield, host: UserCog, member: User };
  const roleColors = { admin: "bg-red-100 text-red-700", host: "bg-blue-100 text-blue-700", member: "bg-slate-100 text-slate-700" };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="users-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">User Management</h1>
            <p className="text-muted-foreground mt-1">{users.length} users</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: primaryColor }} data-testid="add-user-btn">
                <Plus className="mr-2 h-4 w-4" /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
                <DialogDescription>
                  {editingUser ? "Update user details" : "Create a new system user"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="user-name-input"
                  />
                </div>
                {!editingUser && (
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="user-email-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="host">Visitor Host</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{editingUser ? "New Password (leave blank to keep)" : "Password"}</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingUser ? "Leave blank to keep current" : "changeme123"}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" style={{ backgroundColor: primaryColor }}>{editingUser ? "Update" : "Create"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-3">
          {users.map(u => {
            const RoleIcon = roleIcons[u.role] || User;
            return (
              <Card key={u.user_id} className="border-0 shadow-sm" data-testid={`user-card-${u.user_id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-100">
                        <RoleIcon className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-semibold">{u.name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={roleColors[u.role]}>{u.role}</Badge>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.user_id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      {u.user_id !== user?.user_id && (
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(u.user_id)} data-testid={`delete-user-${u.user_id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default UsersPage;
